import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  getMyExternalProfile,
  patchMyExternalProfile,
  type ExternalProfileDto,
} from '../../api/external-profile';
// Cycle 43 — lang-aware date locale
import { formatDateTime } from '../../lib/base-i18n';
import { tSettings, getSettingsLang, subscribeSettingsLang } from '../../lib/settings-i18n';

/**
 * UserExternalProfile 매핑 — Slack / external email / FCM device token.
 *
 * 정책:
 *   - 마운트 시 GET (없으면 default null/null/null)
 *   - 명시 "저장" 버튼 (settings 환경설정 의 debounce 와 다름 — token 등 민감 정보)
 *   - 빈 입력은 null 로 저장 (매핑 해제)
 *   - FCM device token 은 길이 큼 — 별도 textarea
 *   - cycle 59 — cycle 43 의 lang subscribe 자체 구현 → settings-i18n subscribe 통합
 */

export function ExternalProfileSection() {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const [profile, setProfile] = useState<ExternalProfileDto | null>(null);
  const [slackUserId, setSlackUserId] = useState('');
  const [externalEmail, setExternalEmail] = useState('');
  const [fcmDeviceToken, setFcmDeviceToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMyExternalProfile()
      .then((p) => {
        if (cancelled) return;
        setProfile(p);
        setSlackUserId(p.slackUserId ?? '');
        setExternalEmail(p.externalEmail ?? '');
        setFcmDeviceToken(p.fcmDeviceToken ?? '');
      })
      .catch((err: Error) => {
        if (!cancelled) setError(tSettings('settings.external.err_load', { msg: err.message }));
      });
    return () => { cancelled = true; };
  }, []);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await patchMyExternalProfile({
        slackUserId: slackUserId.trim() || null,
        externalEmail: externalEmail.trim() || null,
        fcmDeviceToken: fcmDeviceToken.trim() || null,
      });
      setProfile(updated);
      setSuccess(tSettings('settings.msg.saved_punct'));
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(tSettings('settings.external.err_save', { msg: (err as Error).message }));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="settings-section" aria-label={tSettings('settings.external.heading')}>
      <h2>{tSettings('settings.external.heading')}</h2>
      <p className="settings-section-desc">
        {tSettings('settings.external.desc')}
        {profile?.updatedAt && (
          <span>{tSettings('settings.external.last_updated', { date: formatDateTime(profile.updatedAt) })}</span>
        )}
      </p>

      {error && <div className="settings-error" role="alert">{error}</div>}
      {success && <div className="settings-success" role="status">{success}</div>}

      <div className="settings-field">
        <label htmlFor="external-slack">{tSettings('settings.external.label_slack')}</label>
        <input
          id="external-slack"
          type="text"
          value={slackUserId}
          onChange={(e) => setSlackUserId(e.target.value)}
          placeholder={tSettings('settings.external.placeholder_slack')}
          maxLength={50}
        />
      </div>

      <div className="settings-field">
        <label htmlFor="external-email">{tSettings('settings.external.label_email')}</label>
        <input
          id="external-email"
          type="email"
          value={externalEmail}
          onChange={(e) => setExternalEmail(e.target.value)}
          placeholder={tSettings('settings.external.placeholder_email')}
          maxLength={255}
        />
        {/* ★ 판 P — 이 칸이 «메일 알림 켜기»라는 사실을 적는다. 안 적으면 아무도 안 채운다. */}
        <p className="settings-field-hint">{tSettings('settings.external.hint_email')}</p>
      </div>

      <div className="settings-field">
        <label htmlFor="external-fcm">{tSettings('settings.external.label_fcm')}</label>
        <textarea
          id="external-fcm"
          value={fcmDeviceToken}
          onChange={(e) => setFcmDeviceToken(e.target.value)}
          placeholder={tSettings('settings.external.placeholder_fcm')}
          rows={3}
          maxLength={4096}
        />
      </div>

      <button
        type="button"
        className="settings-save"
        onClick={handleSave}
        disabled={saving}
        aria-busy={saving}
      >
        {saving ? tSettings('settings.btn.saving') : tSettings('settings.btn.save')}
      </button>
    </section>
  );
}
